import { useMemo } from 'react';
import { Formik, Form } from 'formik';
import { Box, Button } from '@rsos/base-components';
import MultiEntryInput from '@rsos/components/capstone/base/form/inputs/MultiEntryInput/MultiEntryInput';
import { useUpdateEventShareMutation } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { RecipientsRequest } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { formatPhoneToE164 } from '../../../../../../../utils/formatPhoneToE164';
import {
  getValidationSchema,
  CustomEnvelopIcon,
  CustomPhoneIcon,
  MultiInputTag,
  RecipientTag,
} from '../../../utils';
import FormTriggers from './FormTriggers';

interface AddRecipientsFormValues {
  recipients: MultiInputTag[];
  inputValue: string;
}

const AddRecipients = ({
  share,
  setIsEditing,
  existingRecipients,
}: {
  share: EventShare;
  setIsEditing: (isEditing: boolean) => void;
  existingRecipients: RecipientTag[];
}) => {
  const [updateEventShare] = useUpdateEventShareMutation();

  const validationSchema = useMemo(() => {
    return getValidationSchema(existingRecipients);
  }, [existingRecipients]);

  const handleAddRecipients = async (values: AddRecipientsFormValues) => {
    if (values.recipients.length === 0) return;

    try {
      const newRecipients: RecipientsRequest = {
        emails: [],
        phone_numbers: [],
        organization_contacts: [],
        contact_groups: [],
      };

      values.recipients.forEach(tag => {
        if (tag.type === 'email') {
          newRecipients.emails.push(tag.value);
        } else if (tag.type === 'phone') {
          newRecipients.phone_numbers.push(formatPhoneToE164(tag.value));
        } else {
          newRecipients.organization_contacts.push(tag.value);
        }
      });

      await updateEventShare({
        org: share.organization,
        sessionId: share.session_id,
        uri: share.uri,
        recipients: newRecipients,
      }).unwrap();

      notifySuccess('Recipients added successfully');
      setIsEditing(false);
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to add recipients',
      );
      notifyError(errorMessage);
    }
  };

  return (
    <Box mt="12px">
      <Formik
        initialValues={{ recipients: [], inputValue: '' }}
        validationSchema={validationSchema}
        onSubmit={handleAddRecipients}
      >
        {({ values, setFieldValue, errors, isValid }) => (
          <Form>
            <FormTriggers existingRecipients={existingRecipients} />
            <MultiEntryInput
              inputType="mixed"
              value={values.inputValue}
              name="inputValue"
              placeholder="Enter or paste phone numbers/email addresses"
              hiddenValue={values.recipients}
              hiddenInputName="recipients"
              tags={values.recipients}
              updateTags={(tags: MultiInputTag[]) => {
                setFieldValue('recipients', tags);
              }}
              setFieldValue={setFieldValue}
              handleChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setFieldValue('inputValue', e.target.value);
              }}
              validator={() => true}
              icons={{ email: CustomEnvelopIcon, phone: CustomPhoneIcon }}
              error={
                typeof errors.recipients === 'string'
                  ? errors.recipients
                  : undefined
              }
            />
            <Box horizontal mt="12px" justify="flex-end">
              <Button
                btnType="primary"
                size="small"
                type="submit"
                disabled={!isValid || values.recipients.length === 0}
              >
                Add Recipients
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default AddRecipients;
